/**
 * Wallet availability in this browser.
 *
 * Two separate questions, and conflating them is how a donor ends up staring at a button that
 * cannot work:
 *
 *   - *Is the rail permitted?* Decided by the server (`context.paymentMethods`).
 *   - *Can this device do it?* Decided here.
 *
 * Both must be true. Apple's guidelines also require that the Apple Pay button is not shown on a
 * device that cannot present it, which is a rule about honesty as much as branding.
 */

/**
 * Apple Pay is available when the browser exposes `ApplePaySession` and says the device can make
 * payments. That is Safari on Apple hardware; Chrome on a Mac is deliberately excluded, because
 * showing the button there produces a tap that goes nowhere.
 */
export function applePayAvailable(): boolean {
  const session = (globalThis as { ApplePaySession?: { canMakePayments?: () => boolean } })
    .ApplePaySession;
  if (!session?.canMakePayments) return false;
  try {
    return session.canMakePayments();
  } catch {
    return false;
  }
}

/**
 * Google Pay rides on the Payment Request API, which is present in Chromium browsers on Android
 * and desktop. Presence of the API is the most this can honestly assert synchronously; whether a
 * card is enrolled is only knowable from an async `canMakePayment()` against a real payment
 * request, which requires the live rail's parameters.
 */
export function googlePayAvailable(): boolean {
  return typeof (globalThis as { PaymentRequest?: unknown }).PaymentRequest === "function";
}

export type WalletContact = {
  readonly phone?: string;
  readonly email?: string;
  readonly name?: string;
};

/**
 * Contact details a wallet handed back with a completed payment.
 *
 * Used to pre-fill, never to prove. A number that came from an Apple or Google account is a number
 * the donor gave *them*; it is not evidence that the person holding this phone controls it now.
 * The server accordingly still consumes an SMS challenge before it will record a pledge against a
 * number — see `services/api/src/pledge-service.ts`.
 *
 * Nothing populates this on the synthetic rail, so the pledge flow falls through to asking.
 */
export const NO_WALLET_CONTACT: WalletContact = Object.freeze({});
